import { useMemo } from 'react';
import { calculateEfficiencyRankings, findOptimalSkillSet } from '../components/Calculations/MaxScoreMinCost';
import { calculateSkillCost } from '../components/Calculations/SkillCostCalculator';

const useSkillOptimizer = ({
    activeSkills,
    aptitudes,
    isLimiterActive,
    limiterBudget
}) => {
    // --- Efficiency & Limiter Logic ---
    const efficiencyRankings = useMemo(() => {
        // We MUST pass the full activeSkills array to the calculator so it can resolve Base-Upgrade relationships.
        // We tell the calculator to treat both 'active' and 'ignoreCost' as "already acquired" by overriding 'active' to true.
        const mathList = activeSkills.map(item => {
            if (item.ignoreCost || item.active) {
                return { ...item, active: true };
            }
            return item;
        });
        return calculateEfficiencyRankings(mathList, aptitudes);
    }, [activeSkills, aptitudes]);

    // Calculate total spent points on ACTIVE skills
    const currentSpent = activeSkills.reduce((total, item) => {
        if (!item.active) return total;
        // Zero Cost Feature: If ignoreCost is true, it doesn't consume our limiter budget.
        if (item.ignoreCost) return total;

        const baseCost = item.skill.cost || 0;
        return total + calculateSkillCost(baseCost, item.level, item.fastLearner);
    }, 0);

    const optimalSkillIds = useMemo(() => {
        if (!isLimiterActive) return new Set();

        // Calculate remaining budget
        // If we are over budget, we pass 0 to the optimizer (it will likely return only the zero-cost active skills)
        const remainingBudget = Math.max(0, limiterBudget - currentSpent);

        // Create a temporary list for optimization where ACQUIRED skills have 0 cost AND are marked inactive.
        // This mathematically forces the optimizer to include them in the resulting optimal set for free,
        // which perfectly preserves the Base-Upgrade group relationships without throwing away elements.
        const optimizationList = activeSkills.map(item => {
            if (item.active || item.ignoreCost) {
                return {
                    ...item,
                    active: false, // Force optimizer to see it as a candidate
                    skill: { ...item.skill, cost: 0 } // Force it to be selected at no budget cost
                };
            }
            return item;
        });

        return findOptimalSkillSet(optimizationList, aptitudes, remainingBudget);
    }, [activeSkills, aptitudes, limiterBudget, isLimiterActive, currentSpent]);

    const insufficientPoints = isLimiterActive && limiterBudget > 0 && optimalSkillIds.size === 0;
    const isOverBudget = isLimiterActive && limiterBudget > 0 && currentSpent > limiterBudget;

    // Dynamic Rankings Calculation
    // If Limiter is OFF: Use global efficiencyRankings.
    // If Limiter is ON: Filter efficiencyRankings to only include optimalSkillIds, sort by rank, and re-index 1..N.
    const dynamicRankings = useMemo(() => {
        if (!isLimiterActive) return efficiencyRankings;

        const newRankMap = new Map();
        if (!optimalSkillIds || optimalSkillIds.size === 0) return newRankMap;

        // 1. Get all optimal skills with their original rank, EXCLUDING the ones we already acquired!
        const optimalSkillsWithRank = [];
        optimalSkillIds.forEach(id => {
            // Find the original item to check if it's already active or purchased
            const originalItem = activeSkills.find(s => parseInt(s.skill.Id_skill || s.skill.Id, 10) === id);
            if (originalItem && !originalItem.active && !originalItem.ignoreCost) {
                const rank = efficiencyRankings.get(id);
                if (rank !== undefined) {
                    optimalSkillsWithRank.push({ id, rank });
                }
            }
        });

        // 2. Sort by original rank (ascending)
        optimalSkillsWithRank.sort((a, b) => a.rank - b.rank);

        // 3. Assign new ranks 1..N (This ensures visually the "Next Best" always becomes #1)
        optimalSkillsWithRank.forEach((item, index) => {
            newRankMap.set(item.id, index + 1);
        });

        return newRankMap;
    }, [isLimiterActive, efficiencyRankings, optimalSkillIds, activeSkills]);

    return {
        currentSpent,
        insufficientPoints,
        isOverBudget,
        dynamicRankings,
        optimalSkillIds
    };
};

export default useSkillOptimizer;
// Revisado: 2025-12-16
